import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { FullPageLoader } from './components/loading/LoadingStates';
import { PortalLoader } from './portal/PortalLoader';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { AuthPage } from './components/auth/AuthPage';
import { Dashboard } from './components/dashboard/Dashboard';
import { CampaignsPage } from './components/campaigns/CampaignsPage';
import { CampaignDetailPage } from './components/campaigns/CampaignDetailPage';
import { CalendarOAuthCallback } from './components/oauth/CalendarOAuthCallback';
import { GmailOAuthCallback } from './components/oauth/GmailOAuthCallback';
import { AppLayout } from './components/layout/AppLayout';
import { PublicDatasetView } from './components/public/PublicDatasetView';
import { PublicMeetingView } from './components/public/PublicMeetingView';
import { UnsubscribePage } from './components/public/UnsubscribePage';
import { AcceptInvitePage } from './components/public/AcceptInvitePage';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { AutoTracker } from './components/analytics/AutoTracker';
import AnalyticsTabs from './components/analytics/AnalyticsTabs';
import { NotFoundPage } from './components/common/NotFoundPage';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ui/ToastContainer';
import { useToast } from './hooks/useToast';
import { SettingsModalProvider } from './contexts/SettingsModalContext';
import { SuggestionWizardProvider } from './contexts/SuggestionWizardContext';
import { ProductTourProvider } from './contexts/ProductTourContext';
import { GlobalSettingsModal } from './components/settings/GlobalSettingsModal';
import { ProductTour } from './components/tour';
import { isFeatureEnabled, flagsLoaded, loadFeatureFlags } from './config/featureFlags';
import { isPortalAccount } from './utils/provisioning';

const PortalApp = React.lazy(() => import('./portal'));

// Waits for the runtime flag load before deciding — otherwise a direct /portal
// deep link is judged against compile-time defaults and bounces to /datasets.
const PortalGate: React.FC = () => {
  const { userProfile, user } = useAuth();
  const [flagsReady, setFlagsReady] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    // Client-workspace users never mount AppDataProvider (which is what calls
    // loadFeatureFlags for operators) — trigger the load here so flagsLoaded
    // actually resolves. Idempotent: a second call just refreshes the flags.
    loadFeatureFlags().catch(() => {});
    flagsLoaded.then(() => { if (!cancelled) setFlagsReady(true); });
    return () => { cancelled = true; };
  }, []);
  if (!flagsReady) {
    return <PortalLoader />;
  }
  // Declared client workspaces ALWAYS get the portal — the global flag gates
  // rollout/discovery for operator accounts only. Without this, flag-off +
  // client-workspace would redirect-loop against ProtectedRoute's client gate.
  // Unprovisioned accounts are admitted too (SKY-507): ProtectedRoute sends
  // them here for the waitlist screen, so a flag-off env must not bounce them
  // back (same loop-avoidance as the client-workspace clause).
  return isFeatureEnabled('clientPortal') || isPortalAccount(userProfile, user?.isAdmin) ? (
    <React.Suspense fallback={<PortalLoader />}>
      <PortalApp />
    </React.Suspense>
  ) : (
    <Navigate to="/" replace />
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, userProfile, user } = useAuth();
  const location = useLocation();

  // Show loading state while authentication and profile are being loaded
  if (isLoading) {
    return (
      <FullPageLoader
        message="Just a moment"
        subMessage="Loading your workspace..."
      />
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  // Client workspaces live in the portal ONLY — declared org flag, never inferred.
  // Segment-exact match so a future /portal-something sibling isn't captured.
  const inPortal = location.pathname === '/portal' || location.pathname.startsWith('/portal/');
  // Invite acceptance must stay reachable for gated accounts: an invited
  // teammate signs up fresh (awaiting-setup by definition) and the waitlist
  // has no path to the invite — bouncing them here would strand the invite
  // permanently (658 review P1).
  const inInvite = location.pathname.startsWith('/invite/');
  // Fresh signups route like clients (SKY-507): to the portal, where the
  // waitlist renders instead of the deprecated dashboard. Provisioning =
  // flipping the account to a client workspace (→ portal proper), granting
  // admin (→ operator surfaces), or grandfathering via onboarding_completed.
  const portalAccount = isPortalAccount(userProfile, user?.isAdmin);
  if (portalAccount && !inPortal && !inInvite) {
    return <Navigate to="/portal" replace />;
  }
  // Clients never mount the operator data plane (credits/datasets/campaigns
  // loads + realtime subscriptions) — the portal runs on portalApi + AuthContext.
  if (portalAccount) {
    return <>{children}</>;
  }

  return (
    <AppDataProvider>
      <SettingsModalProvider>
        <SuggestionWizardProvider>
          <ProductTourProvider>
            <GlobalSettingsModal />
            <ProductTour />
            {children}
          </ProductTourProvider>
        </SuggestionWizardProvider>
      </SettingsModalProvider>
    </AppDataProvider>
  );
};

function AppContent() {
  const { toasts, removeToast } = useToast();

  return (
    <AuthProvider>
      <AutoTracker 
        trackClicks={true}
        trackFormSubmissions={true}
        trackScrolls={true}
        trackDownloads={true}
        trackErrors={true}
        excludeSelectors={[
          '.no-track',
          '[data-no-track]',
          '.loading-spinner',
          '.cursor-pointer:not(button):not(a):not([role="button"])'
        ]}
        debounceMs={200}
      >
          <BrowserRouter>
            <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Navigate to="/datasets" replace />
              </ProtectedRoute>
            } />
            {/* Hidden client portal — flag-gated, never linked from nav (journey-map spec) */}
            <Route path="/portal/*" element={
              <ProtectedRoute>
                <PortalGate />
              </ProtectedRoute>
            } />
            <Route path="/datasets" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/datasets/:tableId" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute>
                <AppLayout title="Campaigns" subtitle="Email templates & outreach">
                  <CampaignsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/campaigns/:templateId" element={
              <ProtectedRoute>
                <CampaignDetailPage />
              </ProtectedRoute>
            } />
            {/* Operator surfaces live in the admin area — the board is a tab there now */}
            <Route path="/pipeline" element={<Navigate to="/analytics?view=pipeline" replace />} />
            <Route path="/admin/signups" element={<Navigate to="/analytics?view=signups" replace />} />
            {/* Command Center — admin only, Pipeline is the default tab */}
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AppLayout title="Command Center" subtitle="Operator surfaces">
                  <AnalyticsTabs />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Invite acceptance — requires auth so the backend matches the invited email */}
            <Route path="/invite/:token" element={
              <ProtectedRoute>
                <AcceptInvitePage />
              </ProtectedRoute>
            } />

            {/* Redirect legacy /accounts routes to /analytics */}
            <Route path="/accounts" element={<Navigate to="/analytics" replace />} />
            <Route path="/accounts/:entityId" element={<Navigate to="/analytics" replace />} />

            {/* Public routes - no authentication required */}
            <Route path="/shared/:shareToken" element={
              <AppDataProvider>
                <SettingsModalProvider>
                  <PublicDatasetView />
                </SettingsModalProvider>
              </AppDataProvider>
            } />
            <Route path="/shared-meeting/:shareToken" element={<PublicMeetingView />} />
            <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
            <Route path="/oauth/gmail/callback" element={<GmailOAuthCallback />} />
            <Route path="/oauth/calendar/callback" element={<CalendarOAuthCallback />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />

            {/* Catch-all route for 404 pages */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>

          <ToastContainer toasts={toasts} onRemove={removeToast} />
      </AutoTracker>
    </AuthProvider>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
